// What a seed's browser edits will lose on the way into Live. Pure.
//
// A browser save keeps each timeline clip's own audio with its warp and pitch
// as settings (seed-history.ts `audioIs: "source"`). Live can rebuild the
// clip's position, length, trims and warp mode, but not the audio's tempo
// (no warp-marker setter — Live warps the file itself) and not clip pitch
// (no setter). Unwarped, unpitched clips arrive exactly as they play here.

import type { Snapshot } from "@shared/seed-history.js";

export interface LiveDifferences {
  /** Warped in the browser: Live re-warps them by its own reading. */
  warped: number;
  /** Pitch-shifted in the browser: Live plays them at the original pitch. */
  pitched: number;
}

export function liveDifferences(arrangement: Snapshot | null | undefined): LiveDifferences {
  let warped = 0;
  let pitched = 0;
  for (const t of arrangement?.audioTracks ?? []) {
    for (const c of t.arrangementClips) {
      // A published render carries the warp and pitch into Live as they play here.
      if (c.audioIs !== "source" || c.render) continue;
      if (c.warping) warped++;
      if ((c.transpose ?? 0) !== 0 || (c.detune ?? 0) !== 0) pitched++;
    }
  }
  return { warped, pitched };
}

/** One sentence, or null when Live will play it exactly as here. */
export function liveDifferencesText(d: LiveDifferences): string | null {
  const parts: string[] = [];
  if (d.warped > 0) parts.push(`${d.warped} warped clip${d.warped === 1 ? "" : "s"} will be re-warped by Live`);
  if (d.pitched > 0) parts.push(`${d.pitched} pitch-shifted clip${d.pitched === 1 ? "" : "s"} will play at the original pitch`);
  if (parts.length === 0) return null;
  return `Heads up: ${parts.join(", and ")}, so ${d.warped + d.pitched === 1 ? "it" : "they"} may sound a little different in Live.`;
}
